import { Component, Inject } from '@angular/core';
import { UploadDocumentService } from '../shared/services/upload-document.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActionDocuments } from '../shared/models/action-documents';
import { MatTableDataSource } from '@angular/material/table';
import { CountryFieldRequirementService } from '../shared/services/country-field-requirement.service';

// Fallback shown for every country outside the 7 Indicator-mechanism countries.
const DEFAULT_DOCUMENT_TYPE_OPTIONS: { value: string; label: string }[] = [
    { value: 'DrivingLicence', label: 'Driving Licence' },
    { value: 'ID', label: 'ID Card' },
    { value: 'Passport', label: 'Passport' },
    { value: 'Other', label: 'Other' },
];

// PascalCase seed DocumentType values (e.g. "ZanzibarResidentCard") -> readable labels
// (e.g. "Zanzibar Resident Card"). Short acronym-style values (e.g. "NIN", "Bvn") are
// left mostly as-is since there's no separate display-name column in the seed data.
function toLabel(documentType: string): string {
    return documentType.replace(/([a-z])([A-Z])/g, '$1 $2');
}

@Component({
    selector: 'xp-document-list',
    templateUrl: './document-list.component.html',
    styleUrl: './document-list.component.scss'
})
export class DocumentListComponent {
    displayedColumns: string[] = ['document', 'date', 'role', 'side'];
    documents = new MatTableDataSource<ActionDocuments>([]);

    currentSortColumn: string | null = null;
    sortDirection: 'asc' | 'desc' = 'asc';

    isLoading: boolean = false;
    selectedDocuments: ActionDocuments[] = [];
    selectedOption: string | null = null;
    selectedSide: string | null = null;
    selectedDocumentsFromParent: ActionDocuments[] = [];
    documentType: string = '';
    documentTypeOptions: { value: string; label: string }[] = DEFAULT_DOCUMENT_TYPE_OPTIONS;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            documentType: string; actionId: string, selectedDocuments: ActionDocuments[]; country?: string
        },
        private documentService: UploadDocumentService,
        private dialogRef: MatDialogRef<DocumentListComponent>,
        private countryFieldRequirementService: CountryFieldRequirementService
    ) {
        this.selectedDocumentsFromParent = data.selectedDocuments;
        this.documentType = data.documentType;
    }

    ngOnInit(): void {
        this.fetchDocuments();
        this.loadDocumentTypeOptions();
    }

    // The 7 Indicator-mechanism countries get their own country-specific document types
    // (matching the seeded CountryDocumentIndicator rows); every other country keeps the
    // existing 4 default options.
    private loadDocumentTypeOptions(): void {
        const country = this.data.country?.toUpperCase();

        if (!country) {
            return;
        }

        this.countryFieldRequirementService.getConfig().subscribe(config => {
            const countryOptions = config.documentIndicators
                .filter(i => i.countryCode.toUpperCase() === country)
                .map(i => ({ value: i.documentType, label: toLabel(i.documentType) }));

            if (countryOptions.length > 0) {
                this.documentTypeOptions = [...countryOptions, { value: 'Other', label: 'Other' }];
            }
        });
    }
    
    async fetchDocuments(): Promise<void> {
        this.isLoading = true;
        this.documentService.getDocuments().subscribe(
            (res) => {
                this.documents.data = res.map((doc) => {
                    const previouslySelected = this.selectedDocumentsFromParent.find((d) => d.id === doc.id);
                    return {
                        ...doc,
                        selectedOption: previouslySelected?.selectedOption ?? '',
                        selectedSide: previouslySelected?.selectedSide ?? '',
                    };
                });
                this.isLoading = false;
            },
            () => {
                this.isLoading = false;
            }
        );
    }

    sortDocuments(field: 'name' | 'date') {
        if (this.currentSortColumn === field) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.currentSortColumn = field;
            this.sortDirection = 'asc';
        }
    
        this.documents.data = this.documents.data.sort((a, b) => {
            let valueA: any, valueB: any;
    
            if (field === 'name') {
                valueA = a.name.toLowerCase();
                valueB = b.name.toLowerCase();
            } else if (field === 'date') {
                valueA = new Date(a.modifiedTimestamp).getTime();
                valueB = new Date(b.modifiedTimestamp).getTime();
            }
    
            return this.sortDirection === 'asc'
                ? valueA > valueB ? 1 : valueA < valueB ? -1 : 0
                : valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
        });
    }
        
    getPreviouslySelectedOption(document: ActionDocuments): string | undefined {
        const selected = this.selectedDocumentsFromParent.find((doc) => doc.id === document.id);
        return selected?.selectedOption ?? '';
    }

    getPreviouslySelectedSide(document: ActionDocuments): string | undefined {
        const selected = this.selectedDocumentsFromParent.find((doc) => doc.id === document.id);
        return selected?.selectedSide ?? '';
    }

    closeDialog(): void {
        this.dialogRef.close();
    }

    toggleSelection(document: ActionDocuments, isChecked: boolean): void {
        if (!this.isAllowedExtension(document)) {
            return;
        }

        if (isChecked) {
            document.selectedOption = this.selectedOption ?? document.selectedOption ?? '';
            document.selectedSide = this.selectedSide ?? document.selectedSide ?? '';

            if (!this.selectedDocuments.includes(document)) {
                this.selectedDocuments.push(document);
            }
        } else {
            this.selectedDocuments = this.selectedDocuments.filter(
                selected => selected !== document
            );
            // Remove from selectedDocuments without clearing fields
            this.selectedDocuments = this.selectedDocuments.filter(doc => doc.id !== document.id);
        }
    }
    // Check if all selected documents have required fields filled
    allFieldsValid(): boolean {
        return this.selectedDocuments.every(doc => doc.selectedSide && doc.selectedOption);
    }

    // Check if a document is selected
    isSelected(document: ActionDocuments): boolean {
        return this.selectedDocuments.includes(document)
            ||
            this.selectedDocumentsFromParent.some(doc => doc.id === document.id);
    }

    allDocumentsSelected(): boolean {
        this.selectedDocuments.length > 0 && this.allFieldsValid();
        if (this.documentType === 'POA') {
            // For POA, you don't need to check Document Type and Document Side.Just check if there are any documents selected.
            return this.selectedDocuments.length > 0;
        }
        // Check if all selected documents have both selectedOption and selectedSide filled
        return this.selectedDocuments.length > 0 &&
            this.selectedDocuments.every(
                (doc) => doc.selectedOption &&
                    (doc.selectedOption === 'Passport' || doc.selectedSide)
            );
    }

    isAllowedExtension(document: ActionDocuments): boolean {
        return !this.selectedDocumentsFromParent.some(doc => doc.id === document.id);

    }


    addSelectedDocuments(): void {
        this.selectedDocuments.forEach(doc => {
            if (doc.selectedOption === 'Passport') {
                doc.selectedSide = 'INNER';
            }
            doc.documentType = this.selectedDocuments === this.selectedDocuments ? 'POI' : 'POA';
            // For POA, ensure selectedOption is set to 'Other'
            if (this.documentType === 'POA' && !doc.selectedOption) {
                doc.selectedOption = 'Other';
            }
        });
        this.dialogRef.close(this.selectedDocuments);
    }

    isPOASelected(): boolean {
        return this.documentType === 'POA';
    }

    removeSelectedDocuments(): void {
        // Clear the selected documents array
        this.selectedDocuments = [];
        this.documents.data.forEach(document => {
            // Check if the document is enabled (not disabled)
            if (this.isAllowedExtension(document)) {
                // Clear the values of selectedOption and selectedSide only for enabled documents
                document.selectedOption = '';
                document.selectedSide = '';
            }
        });

        // Reset the selectedOption and selectedSide variables (if necessary)
        this.selectedOption = null;
        this.selectedSide = null;
    }


}




